import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { LOADING_STATUS } from "../../common/constants";
import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";
import { formatDateToFinnish } from "../../common/misc";

export interface RatePriceItem {
  duration: number;
  dailyPrice: number;
  dailyPurchasePrice: number;
  discountedPrice: number;
  isChanged: boolean;
  tempPriceForProcentualDiscount?: number;
}

export interface SeasonRateDetails {
  id?: string;
  seasonRateType?: string;
  isDeleted?: boolean;
  accommodationId: string;
  seasonName: string;
  startDate: string;
  startDateString?: string;
  endDate: string;
  endDateString?: string;
  priceForAdditionalDay: number;
  tempPriceForProcentualDiscountForAdditionalDay: number;
  discountedPriceForAdditionalDay: number;
  dailyPurchasePrice: number;
  commissionPercent: number;
  checkInTime: string;
  checkOutTime: string;
  ratePrices: RatePriceItem[];
  preparationDays: number;
  seasonRateColorHex: string;
  seasonRateColorId: string;
  seasonRateIdToGetCommissionFrom?: string;
  minBookingDuration: number;
  restrictedCheckInWeekdays: number[];
  restrictedCheckOutWeekdays: number[];
  useParentSeasonCheckInAndCheckOutTimes: boolean;
}

export interface SeasonRatesState {
  seasonRateDetails: SeasonRateDetails;
  seasonRateItems: SeasonRateDetails[];
  seasonRateItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: SeasonRatesState = {
  seasonRateDetails: {
    id: "",
    accommodationId: "",
    seasonName: "",
    startDate: "",
    endDate: "",
    isDeleted: false,
    priceForAdditionalDay: 0,
    discountedPriceForAdditionalDay: 0,
    tempPriceForProcentualDiscountForAdditionalDay: 0,
    dailyPurchasePrice: 0,
    commissionPercent: 20,
    checkInTime: "",
    checkOutTime: "",
    ratePrices: [],
    preparationDays: 0,
    seasonRateColorHex: "#FFFFFF",
    seasonRateColorId: "",
    minBookingDuration: 1,
    restrictedCheckInWeekdays: [],
    restrictedCheckOutWeekdays: [],
    useParentSeasonCheckInAndCheckOutTimes: false,
  },
  seasonRateItems: [],
  seasonRateItemsStatus: LOADING_STATUS.IDLE,
};

export const getSeasonRatesAsync = createAsyncThunk(
  "seasonRates/getSeasonRates",
  async (
    {
      accommodationId,
      startFetchingRatesFrom,
    }: { accommodationId: string; startFetchingRatesFrom: Date },
    { rejectWithValue },
  ) => {
    try {
      const accommodationRates = await apiService.get(
        API_ENDPOINTS.RATES.GET_ACCOMMODATION_RATES +
          "?AccommodationId=" +
          accommodationId +
          "&startFetchingRatesFrom=" +
          formatDateToFinnish(startFetchingRatesFrom),
      );
      return { accommodationRates };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateSeasonRateAsync = createAsyncThunk(
  "seasonRates/updateSeasonRate",
  async (seasonRateData: SeasonRateDetails, { rejectWithValue }) => {
    try {
      const savePromise = await apiService.put(
        API_ENDPOINTS.RATES.SET_ACCOMMODATION_RATE,
        seasonRateData,
      );
      return { savePromise };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteSeasonRateAsync = createAsyncThunk(
  "seasonRates/deleteSeasonRate",
  async (seasonRateData: SeasonRateDetails, { rejectWithValue }) => {
    try {
      const deletePromise = await apiService.deleteByQueryId(
        API_ENDPOINTS.RATES.DELETE_ACCOMMODATION_RATE,
        seasonRateData.id,
      );
      return { deletePromise };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveSeasonRateAsync = createAsyncThunk(
  "seasonRates/addSeasonRate",
  async (seasonRateData: SeasonRateDetails, { rejectWithValue }) => {
    try {
      const savePromise = await apiService.post(
        API_ENDPOINTS.RATES.SAVE_ACCOMMODATION_RATE,
        seasonRateData,
      );
      return { savePromise };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const seasonRatesSlice = createSlice({
  name: "pricing",
  initialState,
  reducers: {
    changeStateSeasonRate: (state, action) => {
      const modifiedSeasonRates = action.payload.modifiedSeasonRate;
      state.seasonRateItems = modifiedSeasonRates;
      //state.seasonRateItems[modifiedSeasonRate.id] = modifiedSeasonRate;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSeasonRatesAsync.pending, (state) => {
        state.seasonRateItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSeasonRatesAsync.fulfilled, (state, action) => {
        state.seasonRateItemsStatus = LOADING_STATUS.IDLE;
        state.seasonRateItems = action.payload.accommodationRates.data;
      })
      .addCase(getSeasonRatesAsync.rejected, (state) => {
        state.seasonRateItemsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const { changeStateSeasonRate } = seasonRatesSlice.actions;
export const selectSeasonRateDetails = (state: RootState) =>
  state.seasonRates.seasonRateDetails;
export const selectSeasonRateItems = (state: RootState) =>
  state.seasonRates.seasonRateItems;
export const selectSeasonRateItemsLoadingStatus = (state: RootState) =>
  state.seasonRates.seasonRateItemsStatus;

export default seasonRatesSlice.reducer;
